import i18n from 'i18next'
import { configApi, primerPaymentApi, stripePaymentApi } from '~/shared/api'
import { PlanAddition } from '~/shared/constants/commonConstants'
import {
  PaymentSystem,
  StripeDeclineReason,
  StripeSoftDeclineReason,
} from '~/shared/constants/paymentConstants'
import { getPaymentConfigFromConfigRaw } from '~/shared/helpers/getPaymentConfigFromConfigRaw'
import { getPaymentProcessorName } from '~/shared/helpers/getPaymentProcessorName'
import { getPurchaseFailedEventParams } from '~/shared/helpers/getPurchaseFailedEventParams'
import { getPurchaseStartedEventParams } from '~/shared/helpers/getPurchaseStartedEventParams'
import { getPurchaseSuccessEventParams } from '~/shared/helpers/getPurchaseSuccessEventParams'
import { logSuccessfulPayment } from '~/shared/helpers/logSuccessfulPayment'
import { eventLogger } from '~/shared/services/eventLogger'
import {
  setErrorAction,
  startFetching,
  stopFetching,
} from '~/shared/store/common/actions'
import { selectCohortID, selectScreenName } from '~/shared/store/common/selects'
import {
  selectIsSelectedSubscriptionOneTime,
  selectSelectedSubscriptionPlanID,
  selectSelectedSubscriptionProductID,
  selectSelectedSubscriptionTrialDays,
} from '~/shared/store/subscription/selects'
import { IAppState, TAppDispatchThunk } from '~/shared/store/types'
import { savePlanAdditionsAction } from '~/shared/store/user/actions'
import {
  selectUserPaymentMethod,
  selectUserPaymentSystem,
  selectUserUUID,
} from '~/shared/store/user/selects'

import { GET_PAYMENT_CONFIG, MAKE_UPSELL } from '../actionTypes'
import { selectIsForce3DS } from '../selects'
import { setPaymentConfigAction } from './syncActions'

export function getPaymentConfigAction(): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const cohort = selectCohortID(state)

    dispatch(startFetching(GET_PAYMENT_CONFIG))

    const response = await configApi.getPaymentConfig({
      uuid,
      cohort,
    })

    if (response.success && response.data) {
      const config = getPaymentConfigFromConfigRaw(response.data)
      dispatch(setPaymentConfigAction(config))
    }

    dispatch(stopFetching(GET_PAYMENT_CONFIG))
  }
}

export const makeUpsellAction =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const selectedSubscriptionPlanID = selectSelectedSubscriptionPlanID(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const productId = selectSelectedSubscriptionProductID(state)
    const screenName = selectScreenName(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)
    const paymentProcessor = getPaymentProcessorName(state, paymentMethod)
    const isOneTime = selectIsSelectedSubscriptionOneTime(state)

    dispatch(startFetching(MAKE_UPSELL))

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem,
      productKey: productId,
      isForce3DS,
      paymentProcessor,
    })

    if (isOneTime) {
      const oneTimeResponse = await primerPaymentApi.createInAppPurchase({
        uuid,
        planId: selectedSubscriptionPlanID,
        productName: productId,
      })

      if (
        oneTimeResponse.data?.error === 'user already bought a subscription'
      ) {
        dispatch(savePlanAdditionsAction([PlanAddition.UPSELL]))
        dispatch(stopFetching(MAKE_UPSELL))
        return
      }

      if (oneTimeResponse.success && oneTimeResponse.data?.inapp) {
        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          trialPeriodDays,
          subscriptionId: oneTimeResponse.data.inapp.payment_id,
          paymentMethod,
          paymentSystem,
          productId: oneTimeResponse.data.inapp.product_id,
          productKey: productId,
          screenName,
          isForce3DS,
          paymentProcessor,
        })

        dispatch(savePlanAdditionsAction([PlanAddition.UPSELL]))
        dispatch(stopFetching(MAKE_UPSELL))
        return
      }

      let oneTimeErrorText: string

      switch (oneTimeResponse.data?.decline_code) {
        case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
          oneTimeErrorText = i18n.t('payment.paymentWaiting.insufficientFunds')
          dispatch(setErrorAction(oneTimeErrorText))
          break
        case StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED:
          oneTimeErrorText = i18n.t('payment.paymentWaiting.velocityExceeded')
          dispatch(setErrorAction(oneTimeErrorText))
          break
        case StripeDeclineReason.LOST_CARD:
        case StripeDeclineReason.STOLEN_CARD:
        case StripeDeclineReason.GENERIC_DECLINE_ERROR:
          oneTimeErrorText = i18n.t('payment.paymentWaiting.genericDecline')
          dispatch(setErrorAction(oneTimeErrorText))
          break
        default:
          oneTimeErrorText = i18n.t('commonComponents.commonError')
          dispatch(setErrorAction(oneTimeErrorText))
      }

      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        paymentMethod,
        paymentSystem,
        productKey: productId,
        error: {
          type: oneTimeResponse.data?.error,
          code: oneTimeResponse.data?.decline_code,
          description: oneTimeErrorText,
        },
        isForce3DS,
        paymentProcessor,
      })
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    const dto = {
      uuid,
      planId: selectedSubscriptionPlanID,
      productId,
    }

    const response =
      paymentSystem === PaymentSystem.PRIMER
        ? await primerPaymentApi.makeSecondary(dto)
        : await stripePaymentApi.makeSecondary(dto)

    if (response.data?.error === 'user already bought a subscription') {
      dispatch(savePlanAdditionsAction([PlanAddition.UPSELL]))
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    if (response.success && response.data?.secondary) {
      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        trialPeriodDays,
        subscriptionId: response.data.secondary.subscription_id,
        paymentMethod,
        paymentSystem,
        productId: response.data.secondary.product_id,
        productKey: productId,
        screenName,
        isForce3DS,
        paymentProcessor,
      })

      dispatch(savePlanAdditionsAction([PlanAddition.UPSELL]))
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    let errorText: string

    switch (response.data?.decline_code) {
      case StripeSoftDeclineReason.INSUFFICIENT_FUNDS:
        errorText = i18n.t('payment.paymentWaiting.insufficientFunds')
        dispatch(setErrorAction(errorText))
        break
      case StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED:
        errorText = i18n.t('payment.paymentWaiting.velocityExceeded')
        dispatch(setErrorAction(errorText))
        break
      case StripeDeclineReason.LOST_CARD:
      case StripeDeclineReason.STOLEN_CARD:
      case StripeDeclineReason.GENERIC_DECLINE_ERROR:
        errorText = i18n.t('payment.paymentWaiting.genericDecline')
        dispatch(setErrorAction(errorText))
        break
      default:
        errorText = i18n.t('commonComponents.commonError')
        dispatch(setErrorAction(errorText))
    }

    eventLogger.logPurchaseFailed({
      ...commonPurchaseFailedParams,
      paymentMethod,
      paymentSystem,
      productKey: productId,
      error: {
        type: response.data?.error,
        code: response.data?.decline_code,
        description: errorText,
      },
      isForce3DS,
      paymentProcessor,
    })
    dispatch(stopFetching(MAKE_UPSELL))
  }
